/**
 * Rental Analysis Calculator Utility
 * Centralized functions for calculating rental property metrics
 */

import { generateCashFlowProjection } from "./irrCalculator";

/**
 * Calculate rent based on radio button mode selection
 * @param {string} mode - Selected mode: "ml", "ai", "current_tenant", "custom"
 * @param {Object} values - Object containing rent values for each mode
 * @param {number} values.ml - ML predicted rent
 * @param {number} values.ai - AI/Agent predicted rent
 * @param {number} values.tenant - Current tenant rent
 * @param {number} values.custom - Custom user-entered rent
 * @returns {number} Calculated rent based on mode
 */
export function calculateRentFromMode(mode, values) {
  if (!mode || !values) return 0;

  switch (mode) {
    case "ml":
      return values.ml || 0;
    case "ai":
      return values.ai || 0;
    case "current_tenant":
      return values.tenant || 0;
    case "custom":
      return values.custom || 0;
    default:
      return 0;
  }
}

/**
 * Calculate monthly mortgage payment for P&I (Principal & Interest)
 * @param {number} loanAmount - The loan amount
 * @param {number} mortgageRate - Annual interest rate (as percentage, e.g., 7 for 7%)
 * @param {number} mortgageTerm - Loan term in years (default 30)
 * @returns {number} Monthly P&I payment
 */
export function calculateMonthlyPI(loanAmount, mortgageRate, mortgageTerm = 30) {
  if (loanAmount <= 0) return 0;

  const monthlyInterestRate = mortgageRate / 100 / 12;
  const numberOfPayments = mortgageTerm * 12;

  const monthlyPayment =
    loanAmount *
    (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) /
    (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);

  return monthlyPayment;
}

/**
 * Calculate monthly mortgage payment for I/O (Interest Only)
 * @param {number} loanAmount - The loan amount
 * @param {number} mortgageRate - Annual interest rate (as percentage, e.g., 7 for 7%)
 * @returns {number} Monthly I/O payment
 */
export function calculateMonthlyIO(loanAmount, mortgageRate) {
  if (loanAmount <= 0) return 0;

  const monthlyInterestRate = mortgageRate / 100 / 12;
  return loanAmount * monthlyInterestRate;
}

/**
 * Calculate Cap Rate (Capitalization Rate)
 * Formula: (Annual Net Income / Property Price) * 100
 * @param {number} monthlyRent - Monthly rental income
 * @param {number} monthlyCarryingCosts - Monthly carrying costs (HOA + Taxes)
 * @param {number} propertyPrice - Property purchase price
 * @returns {number} Cap rate as percentage
 */
export function calculateCapRate(monthlyRent, monthlyCarryingCosts, propertyPrice) {
  if (propertyPrice <= 0) return 0;

  const annualNetIncome = (monthlyRent - monthlyCarryingCosts) * 12;
  return (annualNetIncome / propertyPrice) * 100;
}

/**
 * Calculate Total Out of Pocket investment
 * @param {number} downPayment - Down payment amount
 * @param {number} netClosingCosts - Net closing costs
 * @param {number} renovationCost - Renovation cost (optional)
 * @param {number} timeToRenovate - Time to renovate in months (optional)
 * @param {number} monthlyCarryingCosts - Monthly carrying costs during renovation
 * @param {number} monthlyMortgagePayment - Monthly mortgage payment during renovation
 * @returns {number} Total out of pocket investment
 */
export function calculateTotalOutOfPocket(
  downPayment,
  netClosingCosts,
  renovationCost = 0,
  timeToRenovate = 0,
  monthlyCarryingCosts = 0,
  monthlyMortgagePayment = 0
) {
  // Calculate renovation-related costs only if BOTH renovation cost and time > 0
  const renovationRelatedCosts =
    renovationCost > 0 && timeToRenovate > 0
      ? renovationCost + timeToRenovate * (monthlyCarryingCosts + monthlyMortgagePayment)
      : 0;

  return downPayment + netClosingCosts + renovationRelatedCosts;
}

/**
 * Calculate break-even down payment percentage
 * This finds the down payment % needed to achieve zero monthly cashflow
 * Formula: Rent - Mortgage(downPayment) - CarryingCosts = 0
 *
 * @param {number} rent - Monthly rent
 * @param {number} monthlyCarryingCosts - Monthly carrying costs (HOA + Tax)
 * @param {number} propertyPrice - Property price
 * @param {number} mortgageRate - Annual mortgage rate (as percentage)
 * @param {string} paymentType - "P&I" or "I/O"
 * @returns {number} Break-even down payment percentage (0-100), or null if impossible
 */
export function calculateBreakEvenDownPayment(rent, monthlyCarryingCosts, propertyPrice, mortgageRate, paymentType = "P&I") {
  if (propertyPrice <= 0) return null;

  // If rent doesn't even cover carrying costs, impossible to break even
  if (rent <= monthlyCarryingCosts) return null;

  const targetMortgagePayment = rent - monthlyCarryingCosts;

  // Binary search to find the down payment percentage
  let low = 0;
  let high = 100;
  let iterations = 0;
  const maxIterations = 50;
  const tolerance = 1; // $1 tolerance

  while (iterations < maxIterations && (high - low) > 0.01) {
    const mid = (low + high) / 2;
    const downPayment = propertyPrice * (mid / 100);
    const loanAmount = propertyPrice - downPayment;

    let mortgagePayment;
    if (paymentType === "I/O") {
      mortgagePayment = calculateMonthlyIO(loanAmount, mortgageRate);
    } else {
      mortgagePayment = calculateMonthlyPI(loanAmount, mortgageRate, 30);
    }

    const diff = mortgagePayment - targetMortgagePayment;

    if (Math.abs(diff) < tolerance) {
      return mid;
    }

    if (mortgagePayment > targetMortgagePayment) {
      // Need higher down payment (lower loan amount, lower mortgage payment)
      low = mid;
    } else {
      // Need lower down payment (higher loan amount, higher mortgage payment)
      high = mid;
    }

    iterations++;
  }

  // Return the midpoint if we didn't converge perfectly
  const result = (low + high) / 2;

  // If result is > 100%, it's impossible to break even
  return result > 100 ? null : result;
}

/**
 * Calculate IRR for a specific time horizon (e.g., Year 7)
 * @param {Object} params - Calculation parameters
 * @returns {number} IRR as percentage
 */
export function calculateIRR(params) {
  const {
    propertyPrice,
    monthlyRent,
    monthlyCarryingCosts,
    totalOutOfPocket,
    monthlyMortgagePayment,
    mortgageRate,
    mortgageTerm = 30,
    paymentType,
    downPaymentPercent,
    rentGrowthRate = 5,
    propertyAppreciationRate = 6,
    years = 7,
  } = params;

  const projection = generateCashFlowProjection({
    propertyPrice,
    monthlyRent,
    monthlyCarryingCosts,
    totalOutOfPocket,
    monthlyMortgagePayment,
    mortgageRate,
    mortgageTerm,
    paymentType,
    downPaymentPercent,
    rentGrowthRate,
    propertyAppreciationRate,
    years,
  });

  return projection.irr;
}

/**
 * Calculate all rental analysis scenarios
 * @param {Object} params - Analysis parameters
 * @returns {Object} All 4 scenarios (Low/High × P&I/I/O) with metrics
 */
// The financing the Rental Value Analysis table always reports on. The IRR-assumptions modal
// offers five type/term combinations; this table shows two, one per column, and does not follow
// the modal's term.
export const PI_COLUMN_TERM = "30yr Fixed";
export const IO_COLUMN_TERM = "7/1 ARM";

/**
 * Which rates the Rental Value Analysis table runs on, and whether they came from this
 * listing's own override.
 *
 * Exported because two callers need the SAME answer: the table itself, and the client share
 * page, which seeds its rate controls from it so a client opens on the numbers the analyst
 * quoted. A second implementation would let the two drift, which is the whole failure this
 * thread started from.
 *
 * A per-listing rate OVERRIDES the global config. The order used to be the other way round —
 * global first, `|| propertyAnalysis.mortgage_rate` second — and since the global config always
 * carries rates, the `||` short-circuited on the first term every time and the per-listing rate
 * was never read at all. The IRR-assumptions modal saved it and nothing on the page moved.
 *
 * The override only counts when it was entered for EXACTLY the financing a column shows: P&I on
 * the 30-year fixed, or I/O on the 7/1 ARM. The modal offers five type/term combinations and
 * this table reports on two; a rate saved against any of the other three describes financing
 * that is not on screen, so it is ignored rather than printed under the wrong heading. A row
 * carrying a rate but no payment_type/term matches neither and is likewise ignored.
 *
 * The terms themselves are FIXED — the table never follows the modal's term. And the term only
 * ever selects a rate: every P&I option amortises over 30 years, here and in
 * MortgageConfigModal, so "7/1 ARM" is not a 7-year payoff.
 */
export function resolveMortgageRates(mortgageConfig, propertyAnalysis) {
  const globalPiRate = mortgageConfig?.rates?.pi?.["30_year_fixed"] || 6.25;
  const globalIoRate = mortgageConfig?.rates?.io?.["7_1_arm"] || 5.75;
  const overrideRate = propertyAnalysis?.mortgage_rate || null;
  const overrideFor = propertyAnalysis?.payment_type || null;   // "P&I" | "I/O" | null
  const overrideTerm = propertyAnalysis?.mortgage_term || null; // "30yr Fixed" | "7/1 ARM" | "10/1 ARM" | null

  const piIsCustom = !!(overrideRate && overrideFor === "P&I" && overrideTerm === PI_COLUMN_TERM);
  const ioIsCustom = !!(overrideRate && overrideFor === "I/O" && overrideTerm === IO_COLUMN_TERM);

  return {
    piRate: piIsCustom ? overrideRate : globalPiRate,
    ioRate: ioIsCustom ? overrideRate : globalIoRate,
    piTerm: PI_COLUMN_TERM,
    ioTerm: IO_COLUMN_TERM,
    piIsCustom,
    ioIsCustom,
  };
}

export function calculateRentalAnalysis(params) {
  const {
    property,
    prediction,
    propertyAnalysis,
    mlRent,
    agentRent,
    userRent,
    discountedPrice,
    lowRent: lowRentParam,
    highRent: highRentParam,
    mortgageConfig, // Global mortgage config with separate rates
    monthlyAssessment = 0, // Monthly assessment cost (ongoing or fixed-term)
  } = params;

  if (!property || !prediction) return null;

  // Get property price (use discounted if available)
  const propertyPrice = discountedPrice || property.price || property.asking_price || 0;

  // Get carrying costs from property (single source of truth)
  const monthlyTax = property.monthly_tax || 0;
  const monthlyHOA = property.monthly_hoa || 0;
  const monthlyCarryingCosts = monthlyTax + monthlyHOA + monthlyAssessment;

  // Use lowRent and highRent params if provided (from radio button selections)
  // Otherwise fall back to legacy logic for backward compatibility
  let lowRent, highRent;

  if (lowRentParam !== undefined && highRentParam !== undefined) {
    // Use the params directly - they already respect radio button selections
    lowRent = lowRentParam;
    highRent = highRentParam;
  } else {
    // Legacy fallback: calculate from userRent/mlRent/agentRent
    const hasChecklistValues = userRent && mlRent && userRent !== mlRent;
    lowRent = hasChecklistValues
      ? Math.min(mlRent || 0, agentRent || 0)
      : mlRent || agentRent || prediction.predicted_rent || 0;
    highRent = hasChecklistValues
      ? userRent
      : Math.max(mlRent || 0, agentRent || 0) || prediction.predicted_rent || 0;
  }

  const { piRate, ioRate, piTerm, ioTerm, piIsCustom, ioIsCustom } =
    resolveMortgageRates(mortgageConfig, propertyAnalysis);

  const downPaymentPercent = propertyAnalysis?.down_payment_percent ?? mortgageConfig?.down_payment_percent ?? 25;
  const downPayment = propertyPrice * (downPaymentPercent / 100);
  const loanAmount = propertyPrice - downPayment;

  // Calculate P&I and I/O monthly payments with their respective rates
  const monthlyPI = calculateMonthlyPI(loanAmount, piRate, 30);
  const monthlyIO = calculateMonthlyIO(loanAmount, ioRate);

  // Calculate Total Out of Pocket (for IRR calculations)
  const renovationCost = propertyAnalysis?.renovation_cost || 0;
  const timeToRenovate = propertyAnalysis?.time_to_renovate || 0;
  const netClosingCosts = propertyAnalysis?.net_closing_costs || 0;
  const totalOutOfPocket = calculateTotalOutOfPocket(
    downPayment,
    netClosingCosts,
    renovationCost,
    timeToRenovate,
    monthlyCarryingCosts,
    monthlyIO // Use I/O payment for renovation period carrying costs
  );

  // Helper function to calculate IRR Year 7 for I/O scenarios
  const calculateIRRYear7 = (rent) => {
    return calculateIRR({
      propertyPrice,
      monthlyRent: rent,
      monthlyCarryingCosts,
      totalOutOfPocket,
      monthlyMortgagePayment: monthlyIO,
      mortgageRate: ioRate,
      mortgageTerm: 30,
      paymentType: "I/O",
      downPaymentPercent,
      rentGrowthRate: 5,
      propertyAppreciationRate: 6,
      years: 7,
    });
  };

  // Calculate break-even down payment for each scenario (using respective rates)
  const breakEvenLowPI = calculateBreakEvenDownPayment(lowRent, monthlyCarryingCosts, propertyPrice, piRate, "P&I");
  const breakEvenHighPI = calculateBreakEvenDownPayment(highRent, monthlyCarryingCosts, propertyPrice, piRate, "P&I");
  const breakEvenLowIO = calculateBreakEvenDownPayment(lowRent, monthlyCarryingCosts, propertyPrice, ioRate, "I/O");
  const breakEvenHighIO = calculateBreakEvenDownPayment(highRent, monthlyCarryingCosts, propertyPrice, ioRate, "I/O");

  return {
    lowRent: Math.round(lowRent),
    highRent: Math.round(highRent),
    monthlyPI: Math.round(monthlyPI),
    monthlyIO: Math.round(monthlyIO),
    monthlyCarryingCosts: Math.round(monthlyCarryingCosts),
    piRate,
    ioRate,
    piTerm,
    ioTerm,
    piIsCustom,
    ioIsCustom,
    // Low Rent + P&I
    lowPI: {
      rent: Math.round(lowRent),
      mortgage: Math.round(monthlyPI),
      carry: Math.round(monthlyCarryingCosts),
      cashflow: Math.round(lowRent - monthlyPI - monthlyCarryingCosts),
      capRate: calculateCapRate(lowRent, monthlyCarryingCosts, propertyPrice),
      breakEvenDownPayment: breakEvenLowPI,
    },
    // High Rent + P&I
    highPI: {
      rent: Math.round(highRent),
      mortgage: Math.round(monthlyPI),
      carry: Math.round(monthlyCarryingCosts),
      cashflow: Math.round(highRent - monthlyPI - monthlyCarryingCosts),
      capRate: calculateCapRate(highRent, monthlyCarryingCosts, propertyPrice),
      breakEvenDownPayment: breakEvenHighPI,
    },
    // Low Rent + I/O
    lowIO: {
      rent: Math.round(lowRent),
      mortgage: Math.round(monthlyIO),
      carry: Math.round(monthlyCarryingCosts),
      cashflow: Math.round(lowRent - monthlyIO - monthlyCarryingCosts),
      capRate: calculateCapRate(lowRent, monthlyCarryingCosts, propertyPrice),
      irrYear7: calculateIRRYear7(lowRent),
      breakEvenDownPayment: breakEvenLowIO,
    },
    // High Rent + I/O
    highIO: {
      rent: Math.round(highRent),
      mortgage: Math.round(monthlyIO),
      carry: Math.round(monthlyCarryingCosts),
      cashflow: Math.round(highRent - monthlyIO - monthlyCarryingCosts),
      capRate: calculateCapRate(highRent, monthlyCarryingCosts, propertyPrice),
      irrYear7: calculateIRRYear7(highRent),
      breakEvenDownPayment: breakEvenHighIO,
    },
  };
}
